/** When the balance comes back — the one thing that makes a monthly tier
 * feel different from a one-off bonus.
 *
 * A free account's credits return on the 1st, UTC: the server stamps
 * `free_credits_month` off the same clock, so the date is computed here
 * rather than sent. A subscriber's return on their own billing date, which
 * only Lemon Squeezy knows (`renews_at`).
 */

export interface RenewalInput {
  isAuthenticated: boolean
  isSubscribed: boolean
  /** users.renews_at from the profile — ISO string, subscribers only */
  renewsAt: string | null
  now?: Date
}

/** The next date this account's credits are restored, or null when there is
 * nothing honest to promise (anonymous visitors; a subscriber whose renewal
 * date we don't have, or hold a stale one for — Lemon only moves it when the
 * subscription payload arrives, and a day that already passed is worse than
 * no day at all). */
export function creditsReturnOn({ isAuthenticated, isSubscribed, renewsAt, now }: RenewalInput): Date | null {
  if (!isAuthenticated) return null
  const today = now ?? new Date()
  if (isSubscribed) {
    if (!renewsAt) return null
    const date = new Date(renewsAt)
    if (Number.isNaN(date.getTime()) || date.getTime() <= today.getTime()) return null
    return date
  }
  return new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1))
}

/** "September 1" in the interface language (order and month name follow
 * the locale) — no year: the date is always within a month or so, and the
 * year only adds noise. */
export function formatRenewalDate(date: Date, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date)
  } catch {
    return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(date)
  }
}
